import { FC, useState } from 'react';
import { Button, Menu, MenuItem, Switch } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Alert } from '@mui/material';
import { Api } from 'further-types/eis-wizard';
import { useDisclosure } from 'further-ui/hooks';
import NotifyInvestorsTooltip from 'components/NotifyInvestorsTooltip';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import AlertDialog from 'components/AlertDialog';

const useStyles = makeStyles()(() => ({
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  notifyButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

type FormDataType = Pick<
  Api.MutateEisWizardRequest,
  | 'companyId'
  | 'shareAllocationUploadId'
  | 'notifyInvestors'
  | 'createdWithoutCertificates'
>;

type Props = {
  activeStep: number;
  handleBack: () => void;
  handleNext: () => void;
  handleCreateWithoutCertificates: (investmentType: string) => void;
  data: FormDataType;
  setData: (data: unknown) => void;
  isEditPage: boolean;
  isFinalStep: boolean;
};

const FormActionButtons: FC<Props> = ({
  activeStep,
  handleBack,
  handleNext,
  handleCreateWithoutCertificates,
  data,
  setData,
  isEditPage,
  isFinalStep,
}) => {
  const { classes } = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const confirmDisclosure = useDisclosure<{ investmentType: string }>();
  const [canCreateWithoutCertificates] = useHasPermissions([
    RolePermission.CreateSeisWithoutCertificates,
  ]);

  if (data.createdWithoutCertificates) {
    return (
      <div className={classes.buttonRow}>
        <Button color="primary" variant="outlined" onClick={handleBack}>
          Cancel
        </Button>
        <Alert severity="info">
          This allocation has been completed without generating certificates.
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.buttonRow}>
      <Button color="primary" variant="outlined" onClick={handleBack}>
        {activeStep === 0 ? 'Cancel' : 'Back'}
      </Button>
      <div className={classes.notifyButton}>
        {activeStep === 1 && !isEditPage && (
          <NotifyInvestorsTooltip title="If toggled, the system will send an email to each applicable investor notifying them that a new EIS certificate is available to view and download on their investor portal.">
            <Switch
              checked={data?.notifyInvestors}
              onChange={(event: any) =>
                setData({
                  ...data,
                  notifyInvestors: event.target.checked,
                })
              }
            />
          </NotifyInvestorsTooltip>
        )}

        {canCreateWithoutCertificates && activeStep === 0 && !isEditPage && (
          <>
            <Button
              color="primary"
              variant="outlined"
              aria-controls="simple-menu"
              aria-haspopup="true"
              //@ts-expect-error
              onClick={(event) => setMenuAnchorEl(event.currentTarget)}
              disabled={!data.companyId || !data.shareAllocationUploadId}
              style={{ marginRight: '0.5rem' }}
            >
              Complete without certificates
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null);
                  confirmDisclosure.stageAction({ investmentType: 'EIS' });
                }}
              >
                EIS subscription
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null);
                  confirmDisclosure.stageAction({ investmentType: 'SEIS' });
                }}
              >
                SEIS subscription
              </MenuItem>
            </Menu>
            <AlertDialog
              open={confirmDisclosure.isOpen}
              onClose={confirmDisclosure.onClose}
              title="Complete without certificates"
              btnLabels={{ cancel: 'Cancel', confirm: 'Complete anyway' }}
              onConfirm={() => {
                handleCreateWithoutCertificates(
                  confirmDisclosure.actionProps.investmentType,
                );
                confirmDisclosure.onClose();
              }}
              content="You are about to complete this process without generating certificates. If this was a mistake, click Cancel in this pop-up and select the green Next button on the main page to create certificates as normal."
            />
          </>
        )}

        <Button
          color="primary"
          variant="contained"
          onClick={handleNext}
          disabled={!data.companyId || !data.shareAllocationUploadId}
        >
          {isFinalStep ? 'Submit' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default FormActionButtons;
